import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL;
const SW_PATH = '/sw-push.js';

/** Web Push helpers — kept framework-agnostic so any component can call them.
 *  All functions resolve to a plain object (never throw); UI decides what to
 *  display based on the returned `status`. */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermissionState() {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

async function ensureServiceWorker() {
  // Idempotent — returns the existing registration if there is one.
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_PATH);
}

async function fetchPublicKey() {
  const { data } = await axios.get(`${API_URL}/api/push/vapid-public-key`);
  if (!data?.public_key) throw new Error('VAPID key missing on server');
  return data.public_key;
}

export async function enablePushNotifications({ silent = false } = {}) {
  if (!pushSupported()) {
    if (!silent) toast.error('Tarayıcınız bildirimi desteklemiyor');
    return { status: 'unsupported' };
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      if (!silent) toast.info('Bildirim izni verilmedi');
      return { status: 'denied' };
    }
    const reg = await ensureServiceWorker();
    const existing = await reg.pushManager.getSubscription();
    let subscription = existing;
    if (!subscription) {
      const publicKey = await fetchPublicKey();
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    const payload = subscription.toJSON();
    await axios.post(`${API_URL}/api/push/subscribe`, {
      endpoint: payload.endpoint,
      keys: { p256dh: payload.keys.p256dh, auth: payload.keys.auth },
    }, { withCredentials: true });
    if (!silent) toast.success('Bildirimler etkinleştirildi');
    return { status: 'enabled', endpoint: payload.endpoint };
  } catch (err) {
    if (!silent) toast.error('Bildirim etkinleştirilemedi');
    return { status: 'error', error: err?.message || String(err) };
  }
}

export async function disablePushNotifications() {
  if (!pushSupported()) return { status: 'unsupported' };
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) return { status: 'not_subscribed' };
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { status: 'not_subscribed' };
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    try {
      await axios.delete(`${API_URL}/api/push/subscribe`, { data: { endpoint }, withCredentials: true });
    } catch { /* server prune is best-effort */ }
    toast.success('Bildirimler kapatıldı');
    return { status: 'disabled' };
  } catch (err) {
    toast.error('Kapatılamadı');
    return { status: 'error', error: err?.message || String(err) };
  }
}

export async function getPushSubscriptionState() {
  if (!pushSupported()) return { supported: false, permission: 'unsupported', subscribed: false };
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!sub,
  };
}

export async function sendTestPush() {
  try {
    const { data } = await axios.post(`${API_URL}/api/push/test`, {}, { withCredentials: true });
    if (data.sent > 0) toast.success(`Test bildirimi gönderildi (${data.sent} cihaz)`);
    else toast.info('Aboneliğe sahip cihaz bulunamadı — önce bildirimi etkinleştirin');
    return data;
  } catch (err) {
    toast.error(err?.response?.data?.detail || 'Test başarısız');
    return null;
  }
}
